import React, { useState, useEffect, ChangeEvent } from 'react';
import queryString from 'query-string';

import { FormSubmitionValues, CreateUserResultTypes, PartnerList, PartnerInfo, Platforms, MessageCodes, ReferredPartnerQueryString } from '../../types';
import { DefaultResponse, UserPinResponse, API_STATUS } from '../../legacy/types';
import { finalizeUserEmailParams } from '../../legacy/helpers';
import { fetchPartnerList } from '../../api/fetchPartnerList';
import { fetchPartnerHostNames } from '../../api/fetchPartnerHostnames';
import { fetchPendingUserResults } from '../../api/fetchPendingUserResults';
import { userPin, userFinalize } from '../../legacy/api';
import { getLanguageConfig } from '../../config/text';

import { CrmBlocked, Spinner, SystemError, Timeout, UserNotFound, UserMultipleMatches, UserExists, UserCreateFailed, UserCreated } from '../';
import { Offcode, PinVerified, UserFinalized, ForgotPin, PinSent } from '../../legacy/components';
import { UserSubmit } from '../UserSubmit';
import { MailVerificationSent } from '../MailVerificationSent';

import { getCountryCode } from '../../helpers';

import './style.scss';

const countryCode = getCountryCode();
const textConfig = getLanguageConfig();

export const App = ({ debugMode }: {debugMode: string | undefined}) => {
  const [partnerList, setPartnerList] = useState([] as PartnerList);
  const [formSubmitionValues, setFormSubmitionValues] = useState({
    mobileNumber: '',
    email: '',
    partnerNumber: '',
    customerNumber: '',
    pin: '',
  } as FormSubmitionValues);
  const [partnerInfo, setPartnerInfo] = useState({} as PartnerInfo);
  const [view, setView] = useState(CreateUserResultTypes.SPINNER);
  const [tryAgainView, setTryAgainView] = useState(CreateUserResultTypes.USER_CREATE);
  const [referredPartnerFromQueryString, setRefferedPartnerFromQueryString] = useState('');
  const [flowKey, setFlowKey] = useState('');
  const [accountFirstName, setAccountFirstName] = useState('');

  useEffect(() => {
    const finalizeParams = finalizeUserEmailParams();
    if (finalizeParams === null) {
      getPartnerQueryString();
      fetchPartnerInfo()
        .then(() => { setView(CreateUserResultTypes.USER_CREATE); })
        .catch((err) => {
          console.warn('[App fetchPartnerInfo]', err);
          setView(CreateUserResultTypes.SYSTEM_ERROR);
        });
    } else {
      userFinalize(finalizeParams)
        .then((response: DefaultResponse) => {
          if (response.status === API_STATUS.SUCCESS) {
            userFinalizedSuccess();
          } else {
            console.warn(response);
            fetchPartnerInfo();
            setView(CreateUserResultTypes.USER_CREATE_FAILED);
          }
        }).catch(err => {
          console.warn(err);
          fetchPartnerInfo();
          setView(CreateUserResultTypes.SYSTEM_ERROR);
        });
    }
  }, []);

  const fetchPartnerInfo = () => {
    return Promise.all([fetchPartnerList(countryCode), fetchPartnerHostNames()])
    .then((results) => {
      const [partnerListResults, partnerHostnameResults] = results;
      const partnerList = partnerListResults
      .sort((a, b) => a.name > b.name ? 1 : -1)
      .map((partner) => {
        if (partnerHostnameResults[partner.partnerId]) {
          partner.hostname = partnerHostnameResults[partner.partnerId].hostname;
        } else {
          partner.hostname = window.location.hostname;
          console.warn('delegate/partnerinfo/list partner missing: ', partner);
        }
        return partner;
      });

      if (results) {
        setPartnerList(partnerList);
        Promise.resolve();
      } else {
        Promise.reject();
      }
    });
  };

  const getPartnerQueryString = () => {
    const parsedQueryString: ReferredPartnerQueryString = queryString.parse(window.location.search);
    parsedQueryString.partnerNumber ? setRefferedPartnerFromQueryString(parsedQueryString.partnerNumber) : setRefferedPartnerFromQueryString('');
  };

  const submitPendingUser = (formValues: FormSubmitionValues) => {
    if (partnerInfo.platform === Platforms.BLOCKED) {
      setView(CreateUserResultTypes.CRM_BLOCKED);
      return;
    }

    if (partnerInfo.platform === Platforms.LEGACY) {
      userPin(formValues).then((results: UserPinResponse) => {
        switch (results.status) {
          case API_STATUS.NOT_AUTHENTICATED:
            setView(CreateUserResultTypes.USER_NOT_FOUND);
            break;
          case API_STATUS.SUCCESS:
            if (results.value) {
              if (results.value.accountFirstName) {
                setAccountFirstName(results.value.accountFirstName);
              }
              setFlowKey(results.value.flowKey);
              setView(CreateUserResultTypes.LEGACY_PIN_VERIFIED);
            } else {
              console.warn('Missing property "value" from API.');
              setView(CreateUserResultTypes.SYSTEM_ERROR);
            }
            break;
          case API_STATUS.FAILURE:
            switch (results.messageCode) {
              case MessageCodes.NOT_AUTHENTICATED_6003:
                setView(CreateUserResultTypes.USER_NOT_FOUND);
                break;
              case MessageCodes.USER_ALREADY_EXIST_6004:
                setView(CreateUserResultTypes.USER_EXISTS);
                break;
              default:
                console.warn('[App/index.tsx] API-request user/pin failed with messageCode: ', results.messageCode);
                setView(CreateUserResultTypes.SYSTEM_ERROR);
                break;
            }
            break;
          default:
            console.warn('[App/index.tsx] Unexpected API-status: ', results.status);
            break;
        }
      })
      .catch((error: UserPinResponse) => {
        console.warn('[App/index.tsx] userPin failed: ', error);
        setView(CreateUserResultTypes.USER_CREATE_FAILED);
      });
    } else {
      fetchPendingUserResults(formValues).then((results) => {
        setView(results);
      });
    }
  };

  const tryToSubmitAgain = () => {
    setFormSubmitionValues({
      mobileNumber: '',
      email: '',
      partnerNumber: '',
      customerNumber: '',
      pin: '',
    });
    setPartnerInfo({} as PartnerInfo);
    setView(tryAgainView);
  };

  const userFinalizedSuccess = () => {
    setView(CreateUserResultTypes.LEGACY_USER_FINALIZED);
  };

  const renderViews = () => {
    switch (view) {
      case CreateUserResultTypes.SPINNER:
        return <Spinner textConfig={textConfig}/>;
      case CreateUserResultTypes.USER_EXISTS:
        return <UserExists textConfig={textConfig} partnerHostname={partnerInfo.hostname} />;
      case CreateUserResultTypes.USER_CREATED:
        return <UserCreated textConfig={textConfig} formSubmitionValues={formSubmitionValues} />;
      case CreateUserResultTypes.USER_CREATE_FAILED:
        return <UserCreateFailed textConfig={textConfig} tryToSubmitAgain={tryToSubmitAgain} />;
      case CreateUserResultTypes.USER_NOT_FOUND:
        return (
          <UserNotFound
            textConfig={textConfig}
            formSubmitionValues={formSubmitionValues}
            tryToSubmitAgain={tryToSubmitAgain}
            partnerInfo={partnerInfo}
          />
        );
      case CreateUserResultTypes.USER_MULTIPLE_MATCHES:
        return (
        <UserMultipleMatches
          textConfig={textConfig}
          formSubmitionValues={formSubmitionValues}
          tryToSubmitAgain={tryToSubmitAgain}
          partnerHostname={partnerInfo.hostname}
        />
        );
      case CreateUserResultTypes.MAIL_VERIFICATION_SENT:
        return <MailVerificationSent textConfig={textConfig} />;
      case CreateUserResultTypes.TIMEOUT:
        return <Timeout textConfig={textConfig} partnerHostname={partnerInfo.hostname} />;
      case CreateUserResultTypes.SYSTEM_ERROR:
        return (
          <SystemError
            textConfig={textConfig}
            tryToSubmitAgain={tryToSubmitAgain}
            partnerHostname={partnerInfo.hostname}
          />
        );
      case CreateUserResultTypes.CRM_BLOCKED:
        return <CrmBlocked textConfig={textConfig} partnerInfo={partnerInfo} tryToSubmitAgain={tryToSubmitAgain} />;
      case CreateUserResultTypes.LEGACY_PIN_VERIFIED:
        return (
        <PinVerified
          textConfig={textConfig}
          flowKey={flowKey}
          accountFirstName={accountFirstName}
          setView={setView}
          setTryAgainView={setTryAgainView}
        />
        );
      case CreateUserResultTypes.LEGACY_VERIFY_OFFCODE:
        return (
        <Offcode
          textConfig={textConfig}
          flowKey={flowKey}
          setView={setView}
          userFinalizedSuccess={userFinalizedSuccess}
        />
        );
      case CreateUserResultTypes.LEGACY_USER_FINALIZED:
        return <UserFinalized textConfig={textConfig}/>;
      case CreateUserResultTypes.LEGACY_FORGOT_PIN:
        return (
        <ForgotPin
          textConfig={textConfig}
          partnerList={partnerList}
          partnerInfo={partnerInfo}
          setPartnerInfo={setPartnerInfo}
          setView={setView}
          setTryAgainView={setTryAgainView}
          tryToSubmitAgain={tryToSubmitAgain}
          formSubmitionValues={formSubmitionValues}
          setFormSubmitionValues={setFormSubmitionValues}
        />
        );
      case CreateUserResultTypes.LEGACY_PIN_SENT:
        return <PinSent textConfig={textConfig} tryToSubmitAgain={tryToSubmitAgain} />;
      default:
        return (
        <UserSubmit
            textConfig={textConfig}
            formSubmitionValues={formSubmitionValues}
            setFormSubmitionValues={setFormSubmitionValues}
            partnerInfo={partnerInfo}
            setPartnerInfo={setPartnerInfo}
            partnerList={partnerList}
            submitPendingUser={submitPendingUser}
            referredPartnerNumber={referredPartnerFromQueryString}
            setView={setView}
            setTryAgainView={setTryAgainView}
        />
        );
    }
  };

  // DEBUG/DEV MODE.
  // TODO: AKW-1197
  // run "npm run debug" to display view list
  const renderResultTypes = () => {
    return Object.keys(CreateUserResultTypes).map((view) => {
      return <option key={view}>{view}</option>;
    });
  };

  const viewChanged = (event: ChangeEvent<HTMLSelectElement>) => {
    setView(event.target.value as CreateUserResultTypes);
  };

  return (
    <>
      {debugMode === 'true' && (
        <select id="debugViewList" onChange={viewChanged}>
          {renderResultTypes()}
        </select>
      )}
      <div className="card">{renderViews()}</div>
    </>
  );
};
